// frontend/shared/src/idle.ts —— Console / 代理端空闲自动退出与保活（2026-10-15 用户拍板；H5 不接入）
//
// 纯逻辑：时钟、定时器、存储、跨标签页消息、活动 / 回前台事件源全部经 IdleEnv 注入（vitest 用假实现单测）；
// 浏览器实现见 browserIdleEnv（无 window / document 时返回 null，调用方即不启用空闲计时）。
//
// 判定口径：
// - 「最后活动时刻」= max(本标签页最后活动, localStorage 中各标签页广播的最后活动)；到期判定一律比较时间戳，
//   定时器只负责「到点复核」（页面隐藏后 setTimeout 会被节流，回到前台 visibilitychange 立即复核）。
// - 剩余 ≤ 60 秒弹页面内提示框（倒计时）；提示框打开期间本页的普通活动不算续期（防误触），只有点「继续使用」
//   （refresh 续期）或其他标签页广播的活动才关闭提示框。
// - 到期：本标签页广播「退出」并回调 expire（调用方调 logout 吊销服务端会话、本地退出、提示）。
// - 保活：有活动且距上次向服务端发请求 ≥ 60 秒 → keepAlive（调用方发 GET /me；不得用 refresh，每次 refresh 都会落一行会话记录）。
//   服务端每会话 60s 至多落库一次最后活动，兜底阈值 = 配置值 + 3 分钟余量，可吸收本节流。
// - 启动：localStorage 记录的最后活动已超过持久化的阈值 → bootIdleExpired 为真，调用方直接走空闲退出（boot 时尚未拿到 /me）。
import { readLocal, writeLocal } from './storage';

export type IdleAud = 'admin' | 'agent';
// 跨标签页「退出」原因：logout 手动退出 / idle_timeout 空闲到期 / session_lost 会话失效（过期、改密吊销、最长在线等）
export type IdleLogoutReason = 'logout' | 'idle_timeout' | 'session_lost';

export type IdleMsg = { t: 'act'; at: number } | { t: 'out'; reason: IdleLogoutReason };

export interface IdleStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export interface IdleBus {
  post(msg: IdleMsg): void;
  listen(fn: (msg: IdleMsg) => void): () => void;
  close(): void;
}

export interface IdleEnv {
  now(): number;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  store: IdleStore;
  // 跨标签页消息（BroadcastChannel，不可用时退化为 localStorage storage 事件）；null = 单标签页
  bus: IdleBus | null;
  // 用户活动事件源（mousemove 已节流）；返回退订函数
  onActivity(fn: () => void): () => void;
  // 页面回到前台（visibilitychange → visible / focus）；返回退订函数
  onVisible(fn: () => void): () => void;
}

export interface IdleHooks {
  warn(secondsLeft: number): void; // 提示框出现 / 倒计时刷新
  unwarn(): void; // 提示框关闭
  expire(): void; // 本标签页判定空闲到期（已广播退出）：调用方 logout + 本地退出 + 提示
  remoteLogout(reason: IdleLogoutReason): void; // 其他标签页已退出：调用方只做本地退出
  keepAlive(): void; // 轻量保活请求（GET /api/{aud}/me）
  renew(): Promise<boolean>; // 「继续使用」：refresh 续期，成功返回 true
}

export interface IdleMonitor {
  // 会话建立后启动；已在运行时只更新阈值（不重置最后活动）
  start(idleMinutes: number): void;
  // 本地停止并释放监听 / 定时器；给出 reason 时向其他标签页广播「退出」
  stop(reason?: IdleLogoutReason): void;
  // 宿主卸载：只释放监听 / 定时器，不广播、不清时间戳（保留 boot 判定依据）
  dispose(): void;
  // 任一向服务端的带 access 请求 / refresh 发出时调用（保活节流基准，跨标签页共享）
  noteRequest(): void;
  // 提示框「继续使用」
  continueSession(): Promise<boolean>;
  readonly running: boolean;
  readonly warning: boolean;
}

export const WARN_MS = 60_000; // 到期前 60 秒提示
export const KEEPALIVE_MS = 60_000; // 保活最小间隔
export const MARK_WRITE_MS = 5_000; // 活动 / 请求时间戳写 localStorage 与广播的节流
export const MAX_CHECK_MS = 60_000; // 复核定时器上限（兜底：定时器漂移、storage 事件丢失）
export const MOUSEMOVE_THROTTLE_MS = 1_000;
export const MIN_IDLE_MINUTES = 1;
export const MAX_IDLE_MINUTES = 1440;

export function idleKeys(aud: IdleAud): { at: string; ms: string; srv: string; msg: string; channel: string } {
  return { at: `gpu_idle_${aud}_at`, ms: `gpu_idle_${aud}_ms`, srv: `gpu_idle_${aud}_srv`, msg: `gpu_idle_${aud}_msg`, channel: `gpu_idle_${aud}` };
}

function num(v: string | null): number {
  if (v === null) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// 非法 / 越界的阈值回落到调用方默认值（后端已校验 5–1440；前端只防御旧后端缺字段或脏值）
export function normalizeIdleMinutes(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= MIN_IDLE_MINUTES && v <= MAX_IDLE_MINUTES ? v : fallback;
}

// 启动期判定：上次会话的最后活动已超过持久化阈值（两者缺一即不判定）
export function bootIdleExpired(store: IdleStore, aud: IdleAud, now: number): boolean {
  const k = idleKeys(aud);
  const at = num(store.get(k.at));
  const ms = num(store.get(k.ms));
  return at > 0 && ms > 0 && now - at >= ms;
}

// 会话结束后清掉活动 / 请求时间戳（阈值保留：下次登录前 boot 判定只依赖 at）
export function clearIdleMarks(store: IdleStore, aud: IdleAud): void {
  const k = idleKeys(aud);
  store.remove(k.at);
  store.remove(k.srv);
}

function isIdleMsg(v: unknown): v is IdleMsg {
  if (typeof v !== 'object' || v === null || !('t' in v)) return false;
  if (v.t === 'act') return 'at' in v && typeof v.at === 'number' && Number.isFinite(v.at);
  if (v.t === 'out') return 'reason' in v && (v.reason === 'logout' || v.reason === 'idle_timeout' || v.reason === 'session_lost');
  return false;
}

export function parseIdleMsg(raw: unknown): IdleMsg | null {
  let v: unknown = raw;
  if (typeof raw === 'string') {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return isIdleMsg(v) ? v : null;
}

export function createIdleMonitor(aud: IdleAud, env: IdleEnv, hooks: IdleHooks): IdleMonitor {
  const k = idleKeys(aud);
  let running = false;
  let warning = false;
  let renewing: Promise<boolean> | null = null;
  let idleMs = 0;
  let last = 0; // 本标签页视角的最后活动（含其他标签页广播）
  let lastMarkWrite = 0;
  let lastReq = 0;
  let lastReqWrite = 0;
  let timer: unknown = null;
  let offs: Array<() => void> = [];

  const clearTimer = (): void => {
    if (timer !== null) env.clearTimer(timer);
    timer = null;
  };
  const schedule = (ms: number): void => {
    clearTimer();
    timer = env.setTimer(() => {
      timer = null;
      check();
    }, Math.max(0, Math.min(ms, MAX_CHECK_MS)));
  };
  const teardown = (): void => {
    running = false;
    clearTimer();
    for (const off of offs) {
      try {
        off();
      } catch (e) {
        console.warn('[gp:idle] 退订失败', e);
      }
    }
    offs = [];
    if (warning) {
      warning = false;
      hooks.unwarn();
    }
  };
  const writeMark = (at: number, force: boolean): void => {
    if (!force && at - lastMarkWrite < MARK_WRITE_MS) return;
    lastMarkWrite = at;
    env.store.set(k.at, String(at));
    env.bus?.post({ t: 'act', at });
  };

  function check(): void {
    if (!running) return;
    const now = env.now();
    last = Math.max(last, num(env.store.get(k.at)));
    const left = idleMs - (now - last);
    if (left <= 0) {
      // 续期在途：等待其结果，不抢先退出
      if (renewing) return schedule(1_000);
      teardown();
      env.bus?.post({ t: 'out', reason: 'idle_timeout' });
      clearIdleMarks(env.store, aud);
      hooks.expire();
      return;
    }
    if (left <= WARN_MS) {
      warning = true;
      hooks.warn(Math.ceil(left / 1_000));
      return schedule(left % 1_000 || 1_000); // 对齐到下一整秒刷新倒计时
    }
    if (warning) {
      warning = false;
      hooks.unwarn();
    }
    schedule(left - WARN_MS);
  }

  const noteRequestAt = (now: number): void => {
    lastReq = now;
    if (now - lastReqWrite < MARK_WRITE_MS) return;
    lastReqWrite = now;
    env.store.set(k.srv, String(now));
  };

  const onActivity = (): void => {
    if (!running || warning) return; // 提示框打开期间只认「继续使用」
    const now = env.now();
    last = Math.max(last, now);
    writeMark(now, false);
    const srv = Math.max(lastReq, num(env.store.get(k.srv)));
    if (now - srv >= KEEPALIVE_MS) {
      noteRequestAt(now);
      try {
        hooks.keepAlive();
      } catch (e) {
        console.warn('[gp:idle] 保活请求失败', e);
      }
    }
  };

  const onMessage = (msg: IdleMsg): void => {
    if (!running) return;
    if (msg.t === 'act') {
      last = Math.max(last, msg.at);
      if (warning) check(); // 其他标签页有活动：关闭本页提示框并重排定时器
      return;
    }
    teardown();
    hooks.remoteLogout(msg.reason);
  };

  return {
    get running() {
      return running;
    },
    get warning() {
      return warning;
    },
    start(idleMinutes) {
      const minutes = normalizeIdleMinutes(idleMinutes, MAX_IDLE_MINUTES);
      idleMs = minutes * 60_000;
      env.store.set(k.ms, String(idleMs));
      if (running) {
        check();
        return;
      }
      running = true;
      warning = false;
      const now = env.now();
      last = now; // 会话建立（登录 / 刷新页面恢复）本身即活动
      writeMark(now, true);
      offs = [env.onActivity(onActivity), env.onVisible(() => check())];
      if (env.bus) offs.push(env.bus.listen(onMessage));
      check();
    },
    stop(reason) {
      if (running) teardown();
      if (reason) env.bus?.post({ t: 'out', reason });
      clearIdleMarks(env.store, aud);
    },
    dispose() {
      if (running) teardown();
    },
    noteRequest() {
      noteRequestAt(env.now());
    },
    continueSession() {
      if (!running) return Promise.resolve(false);
      if (renewing) return renewing;
      const p = (async (): Promise<boolean> => {
        let ok = false;
        try {
          ok = await hooks.renew();
        } catch (e) {
          console.warn('[gp:idle] 续期失败', e);
          ok = false;
        }
        renewing = null;
        if (!running) return false;
        if (ok) {
          const now = env.now();
          last = now;
          noteRequestAt(now);
          writeMark(now, true);
          if (warning) {
            warning = false;
            hooks.unwarn();
          }
        }
        check();
        return ok;
      })();
      renewing = p;
      return p;
    },
  };
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'] as const;

// BroadcastChannel 不可用时的退化实现（导出供单测）
export function storageBus(key: string, store: IdleStore): IdleBus {
  const listeners = new Set<(e: StorageEvent) => void>();
  return {
    post(msg) {
      // 附随机数保证同值也触发 storage 事件（storage 事件只在其他标签页触发）
      store.set(key, JSON.stringify({ ...msg, n: Math.random() }));
    },
    listen(fn) {
      const h = (e: StorageEvent): void => {
        if (e.key !== key || e.newValue === null) return;
        const m = parseIdleMsg(e.newValue);
        if (m) fn(m);
      };
      listeners.add(h);
      window.addEventListener('storage', h);
      return () => {
        listeners.delete(h);
        window.removeEventListener('storage', h);
      };
    },
    close() {
      for (const h of listeners) window.removeEventListener('storage', h);
      listeners.clear();
    },
  };
}

function channelBus(name: string): IdleBus | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  let ch: BroadcastChannel;
  try {
    ch = new BroadcastChannel(name);
  } catch (e) {
    console.warn('[gp:idle] BroadcastChannel 不可用，退化为 storage 事件', e);
    return null;
  }
  const listeners = new Set<(e: MessageEvent) => void>();
  return {
    post(msg) {
      try {
        ch.postMessage(msg);
      } catch (e) {
        console.warn('[gp:idle] 广播失败', e);
      }
    },
    listen(fn) {
      const h = (e: MessageEvent): void => {
        const m = parseIdleMsg(e.data);
        if (m) fn(m);
      };
      listeners.add(h);
      ch.addEventListener('message', h);
      return () => {
        listeners.delete(h);
        ch.removeEventListener('message', h);
      };
    },
    close() {
      for (const h of listeners) ch.removeEventListener('message', h);
      listeners.clear();
      ch.close();
    },
  };
}

export const localIdleStore: IdleStore = {
  get: (key) => readLocal(key),
  set: (key, value) => writeLocal(key, value),
  remove: (key) => writeLocal(key, null),
};

// 浏览器环境实现；非浏览器（node 用例、SSR）返回 null。bus 的 close 由 disposeIdleEnv 统一释放
export function browserIdleEnv(aud: IdleAud): IdleEnv | null {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof window.addEventListener !== 'function' || typeof document.addEventListener !== 'function') return null;
  const k = idleKeys(aud);
  const store = localIdleStore;
  const bus = channelBus(k.channel) ?? storageBus(k.msg, store);
  return {
    now: () => Date.now(),
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (h) => {
      if (typeof h === 'number') window.clearTimeout(h);
    },
    store,
    bus,
    onActivity(fn) {
      let lastMove = 0;
      const move = (): void => {
        const now = Date.now();
        if (now - lastMove < MOUSEMOVE_THROTTLE_MS) return;
        lastMove = now;
        fn();
      };
      const opts: AddEventListenerOptions = { capture: true, passive: true };
      document.addEventListener('mousemove', move, opts);
      for (const ev of ACTIVITY_EVENTS) document.addEventListener(ev, fn, opts);
      return () => {
        document.removeEventListener('mousemove', move, opts);
        for (const ev of ACTIVITY_EVENTS) document.removeEventListener(ev, fn, opts);
      };
    },
    onVisible(fn) {
      const vis = (): void => {
        if (document.visibilityState === 'visible') fn();
      };
      document.addEventListener('visibilitychange', vis);
      window.addEventListener('focus', fn);
      return () => {
        document.removeEventListener('visibilitychange', vis);
        window.removeEventListener('focus', fn);
      };
    },
  };
}

export function disposeIdleEnv(env: IdleEnv | null): void {
  try {
    env?.bus?.close();
  } catch (e) {
    console.warn('[gp:idle] 关闭广播通道失败', e);
  }
}
